import { capabilityOf, meetsPlan, type CapabilityId } from '@dale/plans';

import type { FeatureKey, FeatureMap, PermissionKey } from '../../api/schemas';
import type { PlanTier } from '../../lib/plan';

/**
 * The capability of the plan catalog (@dale/plans) each plan-gated
 * destination belongs to, mirroring the `RequirePlan` wrappers in router.tsx
 * one for one. Anything absent is open on every plan.
 */
export const PATH_CAPABILITY: Readonly<Record<string, CapabilityId>> = {
  '/cobrar': 'cobrar',
  '/clientes': 'clientes',
  '/equipo': 'equipo',
  '/clases': 'clases',
  '/ofertas': 'ofertas',
  '/finanzas': 'finanzas',
  '/facturas': 'facturas',
  '/historial': 'historial',
  '/comandas': 'comandas',
  '/mesas': 'mesas',
  '/planes': 'planes',
  '/inteligencia': 'inteligencia',
  '/agentes': 'agentes',
  '/bandeja': 'recepcionista',
};

/** The plan each gated destination needs, from the catalog. */
export const PATH_MIN_PLAN: Readonly<Record<string, PlanTier>> = Object.fromEntries(
  Object.entries(PATH_CAPABILITY).map(([path, id]) => [path, capabilityOf(id).minPlan]),
);

/** The switch each gated destination answers to, so a Dale operator's switch opens or closes it. */
export const PATH_FEATURE: Readonly<Record<string, FeatureKey>> = Object.fromEntries(
  Object.entries(PATH_CAPABILITY).flatMap(([path, id]) => {
    const feature = capabilityOf(id).feature;
    return feature ? [[path, feature]] : [];
  }),
);

/**
 * Whether the business gets `feature`: the server's effective map when it
 * has arrived (an override wins over the plan there), else the plan alone.
 */
export function hasFeature(
  feature: FeatureKey,
  required: PlanTier,
  plan: PlanTier | undefined,
  features: FeatureMap | undefined,
): boolean {
  const answer = features?.[feature];
  return answer ?? meetsPlan(plan, required);
}

/**
 * What a business gets at one capability: open; off, when a Dale operator
 * switched off something the plan includes (the door goes away); locked with
 * the plan that opens it, when the plan falls short (the door stays); or
 * unknown while the business is still loading, when a gated door waits
 * instead of flashing a lock or asking the server for something early.
 */
export type Access = { kind: 'open' } | { kind: 'off' } | { kind: 'unknown' } | { kind: 'locked'; plan: PlanTier };

const OPEN: Access = { kind: 'open' };
const OFF: Access = { kind: 'off' };
const UNKNOWN: Access = { kind: 'unknown' };

export function accessTo(capability: CapabilityId, plan: PlanTier | undefined, features: FeatureMap | undefined): Access {
  if (plan === undefined) return UNKNOWN;
  const { minPlan, feature } = capabilityOf(capability);
  const included = meetsPlan(plan, minPlan);
  const answer = feature ? features?.[feature] : undefined;
  if (answer === true || (answer === undefined && included)) return OPEN;
  if (included) return OFF;
  return { kind: 'locked', plan: minPlan };
}

/**
 * Every plan shows the same doors: one the plan falls short of keeps its
 * place with `lock`, the plan that opens it; only a door a Dale operator
 * switched off goes away (and a gated one waits while the plan is unknown).
 * A link may name its capability when its path alone does not (a
 * restaurant's Menú is /servicios).
 */
export function withPlanAccess<T extends { to: string; capability?: CapabilityId }>(
  links: readonly T[],
  plan: PlanTier | undefined,
  features?: FeatureMap,
): (T & { lock?: PlanTier })[] {
  return links.flatMap((link) => {
    const capability = link.capability ?? PATH_CAPABILITY[link.to];
    if (!capability) return [link];
    const access = accessTo(capability, plan, features);
    if (access.kind === 'off' || access.kind === 'unknown') return [];
    return [access.kind === 'locked' ? { ...link, lock: access.plan } : link];
  });
}

/** Only the doors the business can walk through now, for places that offer no lock. */
export function filterByPlan<T extends { to: string; capability?: CapabilityId }>(
  links: readonly T[],
  plan: PlanTier | undefined,
  features?: FeatureMap,
): T[] {
  return withPlanAccess(links, plan, features).filter((link) => !link.lock);
}

/**
 * The permission each destination needs, mirroring the `RequireAuth
 * permission` wrappers in router.tsx. The owner has every one; a teammate gets
 * what their profile has on (Equipo, Qué ve cada perfil). Anything absent is
 * open to the whole team, except the owner-only paths below.
 */
export const PATH_PERMISSION: Record<string, PermissionKey> = {
  // The team's Inicio is the agenda: without it a teammate opens on their first door instead (HomeRoute).
  '/': 'agenda',
  '/cobrar': 'cobrar',
  '/comandas': 'cobrar',
  '/pagos': 'pagos',
  '/historial': 'pagos',
  '/clientes': 'clientes',
  '/calendario': 'agenda',
  '/servicios': 'servicios',
  '/planes': 'servicios',
  '/ofertas': 'servicios',
  '/mesas': 'servicios',
  '/agenda': 'horario',
  '/equipo': 'horario',
  '/negocio': 'negocio',
  '/finanzas': 'finanzas',
  '/facturas': 'facturas',
  '/inteligencia': 'inteligencia',
};
export const OWNER_ONLY_PATHS = new Set(['/primer-dia', '/herramientas']);

export function filterByPermission<T extends { to: string }>(
  links: readonly T[],
  can: (key: PermissionKey) => boolean,
  owner: boolean,
): T[] {
  return links.filter((link) => {
    if (OWNER_ONLY_PATHS.has(link.to)) return owner;
    const key = PATH_PERMISSION[link.to];
    return key === undefined || can(key);
  });
}
